/**
 * Dev Settings — role assumption hook (spec 0111, Part B).
 *
 * Only active when the app is built in development mode
 * (`import.meta.env.MODE === 'development'`). In production builds Vite
 * tree-shakes the cookie logic and every export is a no-op / null so this
 * module carries zero runtime cost in deployed environments.
 *
 * Two independent axes mirror the real ABAC model:
 *   level → UserLevel → is_director_or_higher
 *   role  → UserRole  → is_product_management / is_project_management
 *
 * All hook instances share state via a custom window event so that setting
 * the role in TopBar is immediately visible to PortfolioDashboardPage (and
 * any other consumer) without a page reload.
 */

import { useCallback, useEffect, useState } from 'react';

import { config } from '@/config';

/** Seniority axis — values align with the backend UserLevel enum where a direct
 *  match exists; "manager" has no UserLevel equivalent (derived from employee_type). */
export type DevAssumedLevel = 'none' | 'lead' | 'manager' | 'director' | 'vp';

/** Functional-group axis — values align with the backend UserRole enum where a
 *  direct match exists; "project_management" and "client_management" have no
 *  UserRole equivalent. */
export type DevAssumedRole =
  'none' | 'pm' | 'project_management' | 'client_management' | 'fullstack' | 'de' | 'ds' | 'ai';

export interface DevRoleState {
  level: DevAssumedLevel;
  role: DevAssumedRole;
}

export interface UseDevRoleOverrideResult {
  roleState: DevRoleState | null;
  setRoleState: (state: DevRoleState) => void;
  clearRoleState: () => void;
}

const COOKIE_NAME = 'dev_assumed_role';
const CHANGE_EVENT = 'dev-role-changed';
const IS_DEV = config.environment === 'development';

function readCookie(): DevRoleState | null {
  try {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${COOKIE_NAME}=`));
    if (!match) {
      return null;
    }
    const raw = decodeURIComponent(match.split('=').slice(1).join('='));
    return JSON.parse(raw) as DevRoleState;
  } catch {
    return null;
  }
}

function writeCookie(state: DevRoleState): void {
  try {
    // No Max-Age = session cookie; cleared on browser close.
    const value = encodeURIComponent(JSON.stringify(state));
    document.cookie = `${COOKIE_NAME}=${value}; path=/; SameSite=Strict`;
    globalThis.dispatchEvent(new CustomEvent<DevRoleState | null>(CHANGE_EVENT, { detail: state }));
  } catch {
    // Restricted cookie context — silently ignore.
  }
}

function deleteCookie(): void {
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; Max-Age=0; SameSite=Strict`;
    globalThis.dispatchEvent(new CustomEvent<DevRoleState | null>(CHANGE_EVENT, { detail: null }));
  } catch {
    // Silently ignore.
  }
}

const noopResult: UseDevRoleOverrideResult = {
  roleState: null,
  setRoleState: () => undefined,
  clearRoleState: () => undefined,
};

/** Returns the current assumed role state and helpers to set or clear it. */
export function useDevRoleOverride(): UseDevRoleOverrideResult {
  // Lazy initializer reads the cookie synchronously on first render so there
  // is no flash of wrong state before a useEffect fires.
  const [roleState, setRoleStateLocal] = useState<DevRoleState | null>(() =>
    IS_DEV ? readCookie() : null,
  );

  // Subscribe to cross-instance updates so all consumers stay in sync when
  // any instance writes or clears the cookie.
  useEffect(() => {
    if (!IS_DEV) {
      return;
    }
    const handler = (e: Event) => {
      setRoleStateLocal((e as CustomEvent<DevRoleState | null>).detail);
    };
    globalThis.addEventListener(CHANGE_EVENT, handler);
    return () => globalThis.removeEventListener(CHANGE_EVENT, handler);
  }, []);

  const setRoleState = useCallback((state: DevRoleState) => {
    if (!IS_DEV) {
      return;
    }
    writeCookie(state);
  }, []);

  const clearRoleState = useCallback(() => {
    if (!IS_DEV) {
      return;
    }
    deleteCookie();
  }, []);

  if (!IS_DEV) {
    return noopResult;
  }

  return { roleState, setRoleState, clearRoleState };
}
